import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getProfessorPracticeStatsDetail } from "../../api/examService";
import {
   getQuestionCategories,
   getQuestionCategoriesWithCount,
} from "../../api/questionCategoriesService";
import {
   fetchUsers,
   removeUser as removeUserAction,
   updateUser as updateUserAction,
} from "../../store/usersSlice";
import { fetchGroups } from "../../store/groupsSlice";
import { UserContext } from "../../UserContext";
import ConfirmDeleteButton from "../Common/ConfirmDeleteButton";
import { ACCOUNT_DELETE_CONFIRM_TITLE } from "../../constants/accountDeletion";
import { closePopup as closePopupUI } from "../Common/popupUIStore";
import UserAvatar from "../Common/UserAvatar";
import ProfileTabs from "../Common/ProfileTabs";
import profileIcon from "../../assets/svg/acc.svg";
import editIcon from "../../assets/svg/material-symbols--edit-outline-sharp.svg";
import statsIcon from "../../assets/svg/mdi--chart-line.svg";
import groupsIcon from "../../assets/svg/material-symbols--group-outline.svg";

const BASE_TABS = [
   { key: "profile", label: "Profil", icon: profileIcon },
   { key: "edit", label: "Editare", icon: editIcon },
];

const STATS_TAB = { key: "stats", label: "Statistică", icon: statsIcon };
const GROUPS_TAB = { key: "groups", label: "Grupe", icon: groupsIcon };
const PROFESSOR_TABS = [
   { key: "profile", label: "Profil", icon: profileIcon },
   STATS_TAB,
   GROUPS_TAB,
];

const COLOR_TOKENS = [
   "--red",
   "--orange",
   "--yellow",
   "--green",
   "--blue",
   "--indigo",
   "--purple",
   "--pink",
];

const COLOR_LABEL = {
   "--red": "Roșu",
   "--orange": "Portocaliu",
   "--yellow": "Galben",
   "--green": "Verde",
   "--blue": "Albastru",
   "--indigo": "Indigo",
   "--purple": "Mov",
   "--pink": "Roz",
};

const ROLE_LABELS = {
   MANAGER: "Manager",
   ADMIN: "Admin",
   PROFESSOR: "Profesor",
};

const AVATAR_COLORS = [
   "hsl(210 90% 88%)",
   "hsl(130 65% 84%)",
   "hsl(54 95% 86%)",
   "hsl(285 100% 86%)",
   "hsl(330 96% 84%)",
   "hsl(30 100% 86%)",
];

const STATUS = {
   PASSED: "PASSED",
   COMPLETED: "COMPLETED",
   FAILED: "FAILED",
   IN_PROGRESS: "IN_PROGRESS",
   NOT_STARTED: "NOT_STARTED",
};

const EMPTY_SUMMARY = {
   total: 0,
   passed: 0,
   failed: 0,
   inProgress: 0,
   passRate: 0,
};

const readEnv = (viteKey, craKey) =>
   (typeof import.meta !== "undefined" &&
      import.meta?.env &&
      import.meta.env[viteKey]) ||
   (typeof process !== "undefined" && process?.env && process.env[craKey]) ||
   "";

const START_ID_RAW = Number(
   readEnv("VITE_TICKETS_START", "REACT_APP_TICKETS_START") || 246,
);
const COUNT_RAW = Number(
   readEnv("VITE_TICKETS_COUNT", "REACT_APP_TICKETS_COUNT") || 269 - 246 + 1,
);
const START_ID = Number.isFinite(START_ID_RAW) ? Math.trunc(START_ID_RAW) : 246;
const COUNT = Number.isFinite(COUNT_RAW) ? Math.max(1, Math.trunc(COUNT_RAW)) : 24;
const TICKET_IDS = Array.from({ length: COUNT }, (_, i) => START_ID + i);
const DISPLAY_BASE = START_ID - 1;

const firstLetter = (value) =>
   String(value || "")
      .trim()
      .charAt(0) || "";

function getRoleLabel(role) {
   const key = String(role || "")
      .trim()
      .toUpperCase();
   return ROLE_LABELS[key] || "Personal";
}

function getColorLabel(token) {
   return COLOR_LABEL[token] || String(token || "").trim() || "Implicit";
}

function getInitials(user) {
   const fn = String(user?.firstName || "").trim();
   const ln = String(user?.lastName || "").trim();
   const a = firstLetter(fn);
   const b = firstLetter(ln);

   if (a && b) return (a + b).toUpperCase();
   return (fn.slice(0, 2) || "–").toUpperCase();
}

function hashStringToUInt(str) {
   let hash = 0;
   for (let i = 0; i < str.length; i += 1) {
      hash = (hash * 31 + str.charCodeAt(i)) | 0;
   }
   return hash >>> 0;
}

function getAvatarColorFromName(user) {
   const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

   if (!/[A-Za-z\u00C0-\u024F\u0400-\u04FF]/.test(fullName)) {
      return AVATAR_COLORS[0];
   }

   const idx =
      hashStringToUInt(fullName.normalize("NFKD")) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

function normalizeColorToCss(value, fallback) {
   const raw = String(value || "").trim();

   if (!raw) return fallback;
   if (raw.startsWith("var(")) return raw;
   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      if (!short || short === "default") return "var(--event-default)";
      if (short.startsWith("event-")) return `var(--${short})`;
      return `var(--event-${short})`;
   }
   if (/^event-/i.test(raw)) return `var(--${raw.toLowerCase()})`;
   return raw;
}

function coerceColorToken(value) {
   const raw = String(value || "").trim();

   if (!raw) return "--blue";
   if (COLOR_TOKENS.includes(raw)) return raw;

   if (raw.startsWith("var(") && raw.includes("--event-")) {
      const match = raw.match(/--event-([a-z-]+)/i);
      if (match) {
         const token = `--${String(match[1]).toLowerCase()}`;
         if (COLOR_TOKENS.includes(token)) return token;
      }
   }

   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      const token = short.startsWith("event-")
         ? `--${short.replace(/^event-/, "")}`
         : `--${short}`;
      if (COLOR_TOKENS.includes(token)) return token;
   }

   if (/^event-/i.test(raw)) {
      const token = `--${raw.replace(/^event-/i, "").toLowerCase()}`;
      if (COLOR_TOKENS.includes(token)) return token;
   }

   const namedToken = `--${raw.toLowerCase()}`;
   if (COLOR_TOKENS.includes(namedToken)) return namedToken;

   return "--blue";
}

function supportsColorForRole(role) {
   const key = String(role || "")
      .trim()
      .toUpperCase();
   return key === "MANAGER" || key === "ADMIN";
}

function createFormData(user, options = {}) {
   const allowColor = options.allowColor !== false;
   return {
      firstName: String(user?.firstName || ""),
      lastName: String(user?.lastName || ""),
      phone: String(user?.phone || ""),
      email: String(user?.email || ""),
      color: allowColor && supportsColorForRole(user?.role)
         ? coerceColorToken(user?.color)
         : "",
   };
}

function normalizePagedResponse(raw) {
   if (Array.isArray(raw)) return raw;
   const items =
      raw?.data ||
      raw?.items ||
      raw?.results ||
      raw?.rows ||
      raw?.categories ||
      [];
   return Array.isArray(items) ? items : [];
}

function normalizeStatus(value) {
   const v = String(value || "").toUpperCase();
   if (v.includes("PASS")) return STATUS.PASSED;
   if (v.includes("COMP")) return STATUS.COMPLETED;
   if (v.includes("FAIL")) return STATUS.FAILED;
   if (v.includes("PROGRESS") || v.includes("STARTED")) {
      return STATUS.IN_PROGRESS;
   }
   if (v.includes("NOT")) return STATUS.NOT_STARTED;
   return STATUS.NOT_STARTED;
}

function statusToState(status) {
   const s = normalizeStatus(status);
   if (s === STATUS.PASSED || s === STATUS.COMPLETED) return "ok";
   if (s === STATUS.FAILED) return "bad";
   return "none";
}

function statusLabelRo(status) {
   const s = normalizeStatus(status);
   if (s === STATUS.PASSED || s === STATUS.COMPLETED) return "Trecut";
   if (s === STATUS.FAILED) return "Picat";
   if (s === STATUS.IN_PROGRESS) return "În lucru";
   return "Neînceput";
}

function parseTicketNr(ticketName) {
   const s = String(ticketName || "");
   let m = s.match(/Practice\s*P\s*([0-9]+)/i);
   if (m) {
      const n = Number(m[1]);
      return Number.isInteger(n) && n > 0 ? n : null;
   }
   m = s.match(/(^|[^0-9])P\s*([0-9]{1,3})([^0-9]|$)/i);
   if (m) {
      const n = Number(m[2]);
      return Number.isInteger(n) && n > 0 ? n : null;
   }
   return null;
}

function parseCategoryId(ticketName) {
   const s = String(ticketName || "");
   let m = s.match(/Category[_\s-]*Practice[_\s-]*([0-9]+)/i);
   if (m) {
      const n = Number(m[1]);
      return Number.isInteger(n) && n > 0 ? n : null;
   }
   m = s.replace(/[\s_-]+/g, "").match(/CategoryPractice([0-9]+)/i);
   if (m) {
      const n = Number(m[1]);
      return Number.isInteger(n) && n > 0 ? n : null;
   }
   return null;
}

function extractTicketNrFromItem(item) {
   const fromName = parseTicketNr(item?.ticketName);
   if (fromName) return fromName;

   const ticketId = Number(item?.ticketId ?? item?.ticket?.id ?? null);
   if (!Number.isInteger(ticketId) || ticketId <= 0) return null;

   const nr = ticketId - DISPLAY_BASE;
   return nr > 0 ? nr : null;
}

function extractCategoryIdFromItem(item) {
   const fromName = parseCategoryId(item?.ticketName);
   if (fromName) return fromName;

   const cid = Number(item?.categoryId ?? item?.category?.id ?? null);
   if (!Number.isInteger(cid) || cid <= 0) return null;
   return cid;
}

function tsOf(item) {
   return (
      Date.parse(
         item?.completedAt ||
            item?.finishedAt ||
            item?.endedAt ||
            item?.startedAt ||
            item?.createdAt ||
            0,
      ) || 0
   );
}

function safeInt(value) {
   if (value == null) return null;
   if (typeof value === "string" && !value.trim()) return null;
   const n = Number(value);
   return Number.isFinite(n) ? n : null;
}

function resolveGroupProfessorId(group) {
   const directId =
      group?.professorId ??
      group?.instructorId ??
      group?.professor?.id ??
      group?.instructor?.id ??
      null;
   const parsed = safeInt(directId);
   return parsed != null ? parsed : null;
}

function resolveGroupMembersCount(group) {
   const directCount =
      safeInt(group?.membersCount) ??
      safeInt(group?.studentsCount) ??
      safeInt(group?.count) ??
      null;
   if (directCount != null && directCount >= 0) return directCount;

   if (Array.isArray(group?.members)) return group.members.length;
   if (Array.isArray(group?.students)) return group.students.length;
   if (Array.isArray(group?.users)) return group.users.length;
   return null;
}

function formatMembersLabel(count) {
   if (!Number.isInteger(count) || count < 0) return "";
   if (count === 1) return "1 cursant";
   return `${count} cursanți`;
}

function buildTicketAgg(practiceHistory) {
   const list = Array.isArray(practiceHistory) ? practiceHistory : [];
   const byNr = new Map();

   for (const item of list) {
      const nr = extractTicketNrFromItem(item);
      if (!nr) continue;

      const st = normalizeStatus(item?.status);
      const ts = tsOf(item);

      const prev = byNr.get(nr) || {
         latestAny: null,
         latestCompleted: null,
         latestInProgress: null,
      };

      if (!prev.latestAny || ts > prev.latestAny.ts) prev.latestAny = { ts, item };

      if (st === STATUS.IN_PROGRESS) {
         if (!prev.latestInProgress || ts > prev.latestInProgress.ts) {
            prev.latestInProgress = { ts, item };
         }
      } else if (!prev.latestCompleted || ts > prev.latestCompleted.ts) {
         prev.latestCompleted = { ts, item };
      }

      byNr.set(nr, prev);
   }

   const out = {};
   for (const [nr, rec] of byNr.entries()) {
      const base =
         rec.latestCompleted?.item ||
         rec.latestInProgress?.item ||
         rec.latestAny?.item ||
         null;

      const st = normalizeStatus(base?.status);
      const state = statusToState(st);
      const correct = safeInt(base?.score);
      const total = safeInt(base?.totalQuestions);

      const hasNewerInProgress =
         rec.latestInProgress &&
         (!rec.latestCompleted || rec.latestInProgress.ts > rec.latestCompleted.ts);

      out[String(nr)] = {
         state,
         st,
         label: hasNewerInProgress ? "În lucru" : statusLabelRo(st),
         correct,
         total,
      };
   }

   return out;
}

function buildCategoryAgg(practiceHistory) {
   const list = Array.isArray(practiceHistory) ? practiceHistory : [];
   const byId = new Map();

   for (const item of list) {
      const categoryId = extractCategoryIdFromItem(item);
      if (!categoryId) continue;

      const st = normalizeStatus(item?.status);
      const ts = tsOf(item);

      const prev = byId.get(categoryId) || {
         latestAny: null,
         latestCompleted: null,
         latestInProgress: null,
      };

      if (!prev.latestAny || ts > prev.latestAny.ts) prev.latestAny = { ts, item };

      if (st === STATUS.IN_PROGRESS) {
         if (!prev.latestInProgress || ts > prev.latestInProgress.ts) {
            prev.latestInProgress = { ts, item };
         }
      } else if (!prev.latestCompleted || ts > prev.latestCompleted.ts) {
         prev.latestCompleted = { ts, item };
      }

      byId.set(categoryId, prev);
   }

   const out = {};
   for (const [categoryId, rec] of byId.entries()) {
      const base =
         rec.latestCompleted?.item ||
         rec.latestInProgress?.item ||
         rec.latestAny?.item ||
         null;

      const st = normalizeStatus(base?.status);
      const state = statusToState(st);
      const correct = safeInt(base?.score);
      const total = safeInt(base?.totalQuestions);

      const hasNewerInProgress =
         rec.latestInProgress &&
         (!rec.latestCompleted || rec.latestInProgress.ts > rec.latestCompleted.ts);

      out[String(categoryId)] = {
         state,
         st,
         label: hasNewerInProgress ? "În lucru" : statusLabelRo(st),
         correct,
         total,
      };
   }

   return out;
}

function getCatCount(category) {
   return (
      category?._count?.questions ??
      category?.questionCount ??
      category?.questionsCount ??
      category?.count ??
      category?.totalQuestions ??
      0
   );
}

function catTitleRo(category) {
   const ro = String(category?.nameRo ?? "").trim();
   const ru = String(category?.nameRu ?? "").trim();
   const name = String(
      category?.name ?? category?.title ?? category?.label ?? "",
   ).trim();
   return ro || name || ru || `#${category?.id}`;
}

function normalizeDisplayText(value) {
   return String(value || "")
      .replace(/[\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/^["'“”„]+|["'“”„]+$/g, "")
      .trim();
}

function toSentenceCase(value, lang = "ro") {
   const clean = normalizeDisplayText(value);
   if (!clean) return "";

   const locale = lang === "ru" ? "ru-RU" : "ro-RO";
   let normalized = clean.toLocaleLowerCase(locale);

   normalized = normalized.replace(
      /(^|[\s(])([a-zа-яăâîșț])\./giu,
      (_, prefix, letter) => `${prefix}${letter.toLocaleUpperCase(locale)}.`,
   );

   normalized = normalized.replace(
      /(^|[.!?]\s+|:\s+|\(\s*)([a-zа-яăâîșț])/giu,
      (_, prefix, letter) => `${prefix}${letter.toLocaleUpperCase(locale)}`,
   );

   return normalized;
}

function buildCategoryTitleParts(title, lang = "ro") {
   const clean = normalizeDisplayText(title);
   if (!clean) return [];

   const markerMatch = clean.match(
      /^\s*((?:\d+\.\d+(?:\.\d+)*\.?|\d+\.))\s*(.*)$/u,
   );

   if (!markerMatch) {
      return [{ marker: null, text: toSentenceCase(clean, lang) }];
   }

   const rawMarker = String(markerMatch[1] || "")
      .replace(/\s+/g, "")
      .trim();
   const marker = rawMarker
      ? rawMarker.endsWith(".")
         ? rawMarker
         : `${rawMarker}.`
      : null;
   const text = toSentenceCase(markerMatch[2], lang);

   return [{ marker, text: text || null }].filter(
      (part) => part.marker || part.text,
   );
}

function normalizeProfessorStatsPayload(raw) {
   const detail = raw?.data !== undefined ? raw.data : raw;
   const summaryRaw =
      detail?.summary && typeof detail.summary === "object"
         ? detail.summary
         : {};
   const total = safeInt(summaryRaw.total);
   const passed = safeInt(summaryRaw.passed);
   const failed = safeInt(summaryRaw.failed);
   const inProgress = safeInt(summaryRaw.inProgress);
   const passRateRaw = Number(summaryRaw.passRate);

   return {
      professor:
         detail?.professor && typeof detail.professor === "object"
            ? detail.professor
            : null,
      summary: {
         total: total != null && total >= 0 ? total : 0,
         passed: passed != null && passed >= 0 ? passed : 0,
         failed: failed != null && failed >= 0 ? failed : 0,
         inProgress: inProgress != null && inProgress >= 0 ? inProgress : 0,
         passRate: Number.isFinite(passRateRaw) ? passRateRaw : 0,
      },
      ticketPractices: Array.isArray(detail?.ticketPractices)
         ? detail.ticketPractices
         : [],
      categoryPractices: Array.isArray(detail?.categoryPractices)
         ? detail.categoryPractices
         : [],
   };
}

function normalizeProfessorPracticeHistory(raw) {
   const detail = normalizeProfessorStatsPayload(raw);
   const ticketPractices = detail.ticketPractices;
   const categoryPractices = detail.categoryPractices;

   const tickets = ticketPractices
      .map((item) => ({
         ticketName: String(item?.ticketName || "").trim(),
         ticketId: safeInt(item?.ticketId),
         categoryId: null,
         status: item?.status || "",
         score: safeInt(item?.score),
         totalQuestions: safeInt(item?.totalQuestions),
         startedAt: item?.startedAt ?? null,
         completedAt: item?.completedAt ?? null,
      }))
      .filter((item) => item.ticketId || parseTicketNr(item.ticketName));

   const categories = categoryPractices
      .map((item) => ({
         ticketName: String(item?.ticketName || "").trim(),
         ticketId: null,
         categoryId: parseCategoryId(item?.ticketName),
         status: item?.status || "",
         score: safeInt(item?.score),
         totalQuestions: safeInt(item?.totalQuestions),
         startedAt: item?.startedAt ?? null,
         completedAt: item?.completedAt ?? null,
      }))
      .filter((item) => item.categoryId);

   return [...tickets, ...categories];
}

function formatSummaryPassRate(value) {
   const numeric = Number(value);
   if (!Number.isFinite(numeric)) return "0%";

   const normalized = numeric <= 1 ? numeric * 100 : numeric;
   const rounded = Math.max(0, Math.min(100, Math.round(normalized * 10) / 10));
   const text = Number.isInteger(rounded)
      ? String(rounded)
      : String(rounded).replace(/\.0$/, "");
   return `${text}%`;
}

export default function StaffProfileUI({ user, mode = "managerAdmin" }) {
   const dispatch = useDispatch();
   const { user: viewer } = useContext(UserContext) || {};
   const userId = String(user?.id ?? "").trim();
   const isProfessorMode = mode === "professor";
   const statsCategoriesRequestRef = useRef(false);

   const { list: users = [], status: usersStatus = "idle" } = useSelector(
      (state) => state.users || {},
   );
   const {
      list: groupsList = [],
      status: groupsStatus = "idle",
      error: groupsError = "",
   } = useSelector((state) => state.groups || {});

   const [tab, setTab] = useState("profile");
   const [saving, setSaving] = useState(false);
   const [deleting, setDeleting] = useState(false);
   const [saveError, setSaveError] = useState("");
   const [formData, setFormData] = useState(() =>
      createFormData(user, { allowColor: !isProfessorMode }),
   );

   const [statsHistory, setStatsHistory] = useState([]);
   const [statsLoading, setStatsLoading] = useState(false);
   const [statsError, setStatsError] = useState("");
   const [statsSummary, setStatsSummary] = useState(EMPTY_SUMMARY);
   const [statsCategories, setStatsCategories] = useState([]);
   const [statsCategoriesLoading, setStatsCategoriesLoading] = useState(false);
   const [statsCategoriesError, setStatsCategoriesError] = useState("");
   const [statsView, setStatsView] = useState("tickets");

   useEffect(() => {
      if (usersStatus === "idle") dispatch(fetchUsers());
   }, [dispatch, usersStatus]);

   useEffect(() => {
      if (!isProfessorMode) return;
      if (groupsStatus === "idle") dispatch(fetchGroups());
   }, [dispatch, groupsStatus, isProfessorMode]);

   const currentUser = useMemo(() => {
      if (!userId) return user || null;

      return (
         (users || []).find((row) => String(row?.id ?? "") === userId) ||
         user ||
         null
      );
   }, [user, userId, users]);

   const professorGroups = useMemo(() => {
      if (!isProfessorMode) return [];

      const uid = safeInt(currentUser?.id);
      if (!uid || !Array.isArray(groupsList)) return [];

      return groupsList
         .filter((group) => resolveGroupProfessorId(group) === uid)
         .map((group) => ({
            id: String(group?.id ?? ""),
            name: String(group?.name || group?.title || "").trim() || "Grupă",
            membersCount: resolveGroupMembersCount(group),
         }))
         .sort((a, b) =>
            a.name.localeCompare(b.name, "ro", { sensitivity: "base" }),
         );
   }, [currentUser?.id, groupsList, isProfessorMode]);

   const showProfessorStats = isProfessorMode;

   const tabs = useMemo(
      () => (isProfessorMode ? PROFESSOR_TABS : BASE_TABS),
      [isProfessorMode],
   );

   useEffect(() => {
      if (!currentUser) return;
      if (!isProfessorMode && tab === "edit") return;

      setFormData(createFormData(currentUser, { allowColor: !isProfessorMode }));
   }, [currentUser, isProfessorMode, tab]);

   useEffect(() => {
      setTab("profile");
      setSaveError("");
      setStatsHistory([]);
      setStatsLoading(false);
      setStatsError("");
      setStatsSummary(EMPTY_SUMMARY);
      setStatsCategories([]);
      setStatsCategoriesLoading(false);
      setStatsCategoriesError("");
      setStatsView("tickets");
      statsCategoriesRequestRef.current = false;
   }, [userId]);

   useEffect(() => {
      if (!showProfessorStats || tab !== "stats") return;
      if (statsCategories.length > 0 || statsCategoriesRequestRef.current) return;

      statsCategoriesRequestRef.current = true;

      (async () => {
         setStatsCategoriesLoading(true);
         setStatsCategoriesError("");
         try {
            let categories = [];
            try {
               const res = await getQuestionCategoriesWithCount();
               categories = Array.isArray(res) ? res : normalizePagedResponse(res);
            } catch {
               categories = [];
            }

            if (!categories.length) {
               const raw = await getQuestionCategories(1, 2000);
               categories = normalizePagedResponse(raw);
            }

            setStatsCategories(categories);
         } catch (error) {
            setStatsCategories([]);
            setStatsCategoriesError(
               error?.message || "Nu am putut încărca categoriile.",
            );
         } finally {
            statsCategoriesRequestRef.current = false;
            setStatsCategoriesLoading(false);
         }
      })();
   }, [showProfessorStats, statsCategories.length, tab]);

   useEffect(() => {
      if (!showProfessorStats || tab !== "stats") return;
      const uid = Number(currentUser?.id);
      if (!Number.isInteger(uid) || uid <= 0) {
         setStatsHistory([]);
         setStatsSummary(EMPTY_SUMMARY);
         setStatsError("");
         setStatsLoading(false);
         return;
      }

      let cancelled = false;

      (async () => {
         setStatsLoading(true);
         setStatsError("");

         try {
            const detail = await getProfessorPracticeStatsDetail(uid);
            if (cancelled) return;
            const normalized = normalizeProfessorStatsPayload(detail?.data);
            setStatsSummary(normalized.summary);
            setStatsHistory(normalizeProfessorPracticeHistory(normalized));
            setStatsError("");
         } catch (error) {
            if (!cancelled) {
               setStatsHistory([]);
               setStatsSummary(EMPTY_SUMMARY);
               setStatsError(error?.message || "Nu am putut încărca statistica.");
            }
         } finally {
            if (!cancelled) setStatsLoading(false);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [currentUser?.id, showProfessorStats, tab]);

   const handleFormChange = (event) => {
      const { name, value } = event.target;
      setFormData((current) => ({
         ...current,
         [name]: value,
      }));
   };

   const handleResetProfile = () => {
      if (!currentUser) return;

      setSaveError("");
      setFormData(createFormData(currentUser, { allowColor: !isProfessorMode }));
      if (!isProfessorMode) setTab("profile");
   };

   const handleSaveEdit = async () => {
      if (!currentUser?.id) return;

      const firstName = String(formData.firstName || "").trim();
      const lastName = String(formData.lastName || "").trim();
      const phone = String(formData.phone || "").trim();
      const email = String(formData.email || "").trim();
      const role = String(currentUser?.role || "")
         .trim()
         .toUpperCase();
      const supportsColor =
         !isProfessorMode && supportsColorForRole(role);

      if (!firstName || !lastName) {
         setSaveError("Completează prenumele și numele.");
         return;
      }

      if (!email) {
         setSaveError("Email este obligatoriu.");
         return;
      }

      setSaving(true);
      setSaveError("");

      try {
         await dispatch(
            updateUserAction({
               id: currentUser.id,
               data: {
                  firstName,
                  lastName,
                  phone,
                  email,
                  color: supportsColor ? coerceColorToken(formData.color) : "",
                  role,
               },
            }),
         ).unwrap();

         await dispatch(fetchUsers());
         setTab("profile");
      } catch (error) {
         setSaveError(error?.message || "Nu am putut salva modificările.");
      } finally {
         setSaving(false);
      }
   };

   // Ștergerea contului de personal: adminul poate șterge pe oricine în afară
   // de el însuși; managerul doar profesorii (nu admini sau alți manageri).
   const viewerRole = String(viewer?.role || "")
      .trim()
      .toUpperCase();
   const targetRole = String(currentUser?.role || "")
      .trim()
      .toUpperCase();
   const isViewerSelf =
      Boolean(viewer?.id) &&
      Boolean(currentUser?.id) &&
      String(viewer.id) === String(currentUser.id);
   const canDeleteUser =
      Boolean(currentUser?.id) &&
      !isViewerSelf &&
      (viewerRole === "ADMIN" ||
         (viewerRole === "MANAGER" &&
            targetRole !== "ADMIN" &&
            targetRole !== "MANAGER"));

   const handleDeleteUser = async () => {
      if (!currentUser?.id || deleting || saving) return;

      setDeleting(true);
      setSaveError("");

      try {
         await dispatch(removeUserAction(currentUser.id)).unwrap();
         dispatch(fetchUsers());
         closePopupUI();
      } catch (error) {
         setSaveError(error?.message || "Nu am putut șterge contul.");
      } finally {
         setDeleting(false);
      }
   };

   const fullName =
      `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() ||
      getRoleLabel(currentUser?.role);
   const roleLabel = getRoleLabel(currentUser?.role);
   const phone = currentUser?.phone || "—";
   const email = currentUser?.email || "—";
   const supportsColor =
      !isProfessorMode && supportsColorForRole(currentUser?.role);
   const rawColor = supportsColor
      ? String(currentUser?.color || "").trim()
      : "";
   const displayColor = normalizeColorToCss(
      rawColor,
      getAvatarColorFromName(currentUser),
   );
   const activeColorToken = coerceColorToken(formData.color);

   const detailRows = [
      {
         key: "firstName",
         label: "Prenume",
         value: currentUser?.firstName || "—",
      },
      { key: "lastName", label: "Nume", value: currentUser?.lastName || "—" },
      { key: "phone", label: "Telefon", value: phone },
      { key: "email", label: "Email", value: email },
      { key: "role", label: "Rol", value: roleLabel },
   ];

   const summaryCards = [
      {
         key: "total",
         label: "Total",
         value: statsSummary.total,
      },
      {
         key: "passed",
         label: "Trecute",
         value: statsSummary.passed,
      },
      {
         key: "failed",
         label: "Picat",
         value: statsSummary.failed,
      },
      {
         key: "inProgress",
         label: "În lucru",
         value: statsSummary.inProgress,
      },
      {
         key: "passRate",
         label: "Rată",
         value: formatSummaryPassRate(statsSummary.passRate),
      },
   ];

   const ticketAgg = useMemo(() => buildTicketAgg(statsHistory), [statsHistory]);
   const categoryAgg = useMemo(
      () => buildCategoryAgg(statsHistory),
      [statsHistory],
   );

   const ticketVm = useMemo(() => {
      return TICKET_IDS.map((id) => {
         const nr = id - DISPLAY_BASE;
         const agg = ticketAgg[String(nr)] || null;
         const state = agg?.state || "none";
         const label = agg?.label || "Neînceput";
         const total =
            Number.isFinite(Number(agg?.total)) && Number(agg.total) > 0
               ? Number(agg.total)
               : null;
         const correct =
            Number.isFinite(Number(agg?.correct)) && Number(agg.correct) >= 0
               ? Math.min(Number(agg.correct), total ?? Number(agg.correct))
               : null;
         const scoreText =
            correct != null && total != null ? `${correct}/${total}` : null;
         const extra = scoreText ? ` • ${scoreText}` : "";

         return {
            id,
            nr,
            state,
            label: label + extra,
            scoreText,
         };
      });
   }, [ticketAgg]);

   const categoryRows = useMemo(() => {
      const byId = new Map();

      for (const category of statsCategories || []) {
         const id = Number(category?.id ?? category?.categoryId ?? null);
         if (!Number.isInteger(id) || id <= 0) continue;
         const title = catTitleRo(category);

         byId.set(id, {
            id,
            name: title,
            titleParts: buildCategoryTitleParts(title, "ro"),
            count: getCatCount(category),
         });
      }

      for (const key of Object.keys(categoryAgg || {})) {
         const id = Number(key);
         if (!Number.isInteger(id) || id <= 0) continue;
         if (byId.has(id)) continue;

         byId.set(id, {
            id,
            name: `Categorie #${id}`,
            titleParts: [{ marker: null, text: `Categorie #${id}` }],
            count: Number(categoryAgg[key]?.total ?? 0) || 0,
         });
      }

      return Array.from(byId.values()).sort((a, b) => a.id - b.id);
   }, [categoryAgg, statsCategories]);

   const catStatById = useMemo(() => {
      const out = {};

      for (const category of categoryRows) {
         const agg = categoryAgg[String(category.id)] || null;
         const state = agg?.state || "none";
         const st = agg?.st || STATUS.NOT_STARTED;

         const totalDisp =
            Number.isFinite(Number(agg?.total)) && Number(agg.total) >= 0
               ? Number(agg.total)
               : Number(category.count) || 0;

         const correctDisp =
            Number.isFinite(Number(agg?.correct)) && Number(agg.correct) >= 0
               ? Number(agg.correct)
               : null;

         out[String(category.id)] = {
            state,
            st,
            total: totalDisp,
            correct: correctDisp,
         };
      }

      return out;
   }, [categoryAgg, categoryRows]);

   const canShowCategoryContent = !statsLoading && !statsError;
   const showCategoryList = canShowCategoryContent && categoryRows.length > 0;
   const showCategoryEmpty =
      canShowCategoryContent &&
      !statsCategoriesLoading &&
      !statsCategoriesError &&
      categoryRows.length === 0;
   const showCategoryError =
      canShowCategoryContent &&
      !statsCategoriesLoading &&
      !!statsCategoriesError &&
      categoryRows.length === 0;
   const showCategoryLoading =
      canShowCategoryContent &&
      statsCategoriesLoading &&
      categoryRows.length === 0;

   const profileEditForm = (
      <form
         className="staffProfileUI__form"
         onSubmit={(event) => {
            event.preventDefault();
            handleSaveEdit();
         }}
      >
         <div className="staffProfileUI__editCard">
            <label className="staffProfileUI__field">
               <span className="staffProfileUI__fieldLabel">
                  Prenume
               </span>
               <input
                  name="firstName"
                  className="staffProfileUI__input"
                  value={formData.firstName}
                  onChange={handleFormChange}
                  placeholder="Prenume"
                  autoComplete="given-name"
               />
            </label>

            <label className="staffProfileUI__field">
               <span className="staffProfileUI__fieldLabel">
                  Nume
               </span>
               <input
                  name="lastName"
                  className="staffProfileUI__input"
                  value={formData.lastName}
                  onChange={handleFormChange}
                  placeholder="Nume"
                  autoComplete="family-name"
               />
            </label>

            <label className="staffProfileUI__field">
               <span className="staffProfileUI__fieldLabel">
                  Telefon
               </span>
               <input
                  name="phone"
                  className="staffProfileUI__input"
                  value={formData.phone}
                  onChange={handleFormChange}
                  placeholder="Telefon"
                  autoComplete="tel"
               />
            </label>

            <label className="staffProfileUI__field">
               <span className="staffProfileUI__fieldLabel">
                  Email
               </span>
               <input
                  name="email"
                  className="staffProfileUI__input"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="Email"
                  autoComplete="email"
               />
            </label>

            {supportsColor && (
               <div className="staffProfileUI__tokenPicker">
                  <span className="staffProfileUI__fieldLabel">
                     Culoare cont
                  </span>

                  <div
                     className="staffProfileUI__tokenGrid"
                     role="radiogroup"
                     aria-label="Culoare cont"
                  >
                     {COLOR_TOKENS.map((token) => {
                        const isActive = activeColorToken === token;

                        return (
                           <button
                              key={token}
                              type="button"
                              role="radio"
                              aria-checked={isActive}
                              className={`staffProfileUI__tokenButton ${
                                 isActive ? "is-active" : ""
                              }`}
                              title={getColorLabel(token)}
                              aria-label={getColorLabel(token)}
                              style={{
                                 background: normalizeColorToCss(
                                    token,
                                    "var(--event-default)",
                                 ),
                              }}
                              onClick={() =>
                                 setFormData((current) => ({
                                    ...current,
                                    color: token,
                                 }))
                              }
                           />
                        );
                     })}
                  </div>
               </div>
            )}
         </div>

         {saveError && (
            <p className="staffProfileUI__error">{saveError}</p>
         )}

         <div className="staffProfileUI__actions">
            {canDeleteUser ? (
               <ConfirmDeleteButton
                  className="staffProfileUI__deleteConfirm"
                  fullWidth={false}
                  onConfirm={handleDeleteUser}
                  disabled={saving || deleting}
                  label={deleting ? "Se șterge..." : "Șterge"}
                  confirmLabel={deleting ? "Se șterge..." : "Da, șterge"}
                  cancelLabel="Nu"
                  confirmTitle={ACCOUNT_DELETE_CONFIRM_TITLE}
               />
            ) : null}
            <button
               type="submit"
               className="staffProfileUI__button staffProfileUI__button--save"
               disabled={saving || deleting}
            >
               {saving ? "Se salvează..." : "Salvează"}
            </button>
            <button
               type="button"
               className="staffProfileUI__button staffProfileUI__button--secondary"
               onClick={handleResetProfile}
               disabled={saving || deleting}
            >
               {isProfessorMode ? "Resetează" : "Anulează"}
            </button>
         </div>
      </form>
   );

   if (!currentUser) {
      return (
         <div className="staffProfileUI">
            <div className="staffProfileUI__header popupPanelUI__header">
               <h3 className=" staffProfileUI__title">
                  Personal
               </h3>
            </div>
            <div className="staffProfileUI__content">
               <p className="staffProfileUI__loading">Se încarcă...</p>
            </div>
         </div>
      );
   }

   return (
      <div className="staffProfileUI">
         <div className="staffProfileUI__header popupPanelUI__header">
            <h3 className=" staffProfileUI__title">
               {roleLabel}
            </h3>
         </div>

         <div className="staffProfileUI__content">
            <div className="staffProfileUI__hero">
               <UserAvatar
                  person={currentUser}
                  color={displayColor}
                  initials={getInitials(currentUser)}
                  className="staffProfileUI__avatar"
               />

               <div className="staffProfileUI__heroMain">
                  <div className="staffProfileUI__heroTitle">
                     <strong className="staffProfileUI__heroName">
                        {fullName}
                     </strong>
                  
                  </div>

                  <div className="staffProfileUI__chips">
                     <span className="staffProfileUI__chip">{email}</span>
                     <span className="staffProfileUI__chip">{phone}</span>
                  </div>
               </div>
            </div>

            <ProfileTabs
               items={tabs}
               value={tab}
               onChange={(key) => {
                  setSaveError("");
                  setTab(key);
               }}
            />

            {tab === "profile" && (
               <div className="staffProfileUI__section">
                  {isProfessorMode ? (
                     profileEditForm
                  ) : (
                     <>
                        <div className="staffProfileUI__details">
                           {detailRows.map((row) => (
                              <div
                                 key={row.key}
                                 className="staffProfileUI__detailRow"
                              >
                                 <span className="staffProfileUI__detailLabel">
                                    {row.label}
                                 </span>
                                 <div className="staffProfileUI__detailValue">
                                    {row.value}
                                 </div>
                              </div>
                           ))}

                           {supportsColor && (
                              <div className="staffProfileUI__detailRow">
                                 <span className="staffProfileUI__detailLabel">
                                    Culoare
                                 </span>
                                 <div className="staffProfileUI__detailValue staffProfileUI__detailValue--color">
                                    <span
                                       className="staffProfileUI__colorSwatch"
                                       style={{ background: displayColor }}
                                       aria-hidden="true"
                                    />
                                    <span>{rawColor || "Implicit"}</span>
                                 </div>
                              </div>
                           )}
                        </div>

                        <div className="staffProfileUI__actions">
                           <button
                              type="button"
                              className="staffProfileUI__button staffProfileUI__button--edit"
                              onClick={() => setTab("edit")}
                           >
                              Edit
                           </button>
                        </div>
                     </>
                  )}
               </div>
            )}

            {tab === "edit" && !isProfessorMode && (
               <div className="staffProfileUI__section">
                  {profileEditForm}
               </div>
            )}

            {tab === "stats" && showProfessorStats && (
               <div className="staffProfileUI__section staffProfileUI__section--stats">
                  <div className="staffProfileUI__statsPanel">
                     <div className="staffProfileUI__summaryGrid">
                        {summaryCards.map((card) => (
                           <div key={card.key} className="staffProfileUI__summaryCard">
                              <span className="staffProfileUI__summaryLabel">
                                 {card.label}
                              </span>
                              <span className="staffProfileUI__summaryValue">
                                 {card.value}
                              </span>
                           </div>
                        ))}
                     </div>

                     <div
                        className={`staffProfileUI__statsSwitch ${
                           statsView === "tickets" ? "is-left" : "is-right"
                        }`}
                     >
                        <button
                           type="button"
                           className={`staffProfileUI__statsSwitchButton ${
                              statsView === "tickets" ? "is-active" : ""
                           }`}
                           onClick={() => setStatsView("tickets")}
                        >
                           Bilete
                        </button>

                        <button
                           type="button"
                           className={`staffProfileUI__statsSwitchButton ${
                              statsView === "categories" ? "is-active" : ""
                           }`}
                           onClick={() => setStatsView("categories")}
                        >
                           Categorii
                        </button>
                     </div>

                     {statsLoading ? (
                        <p className="staffProfileUI__status">Se încarcă statistica...</p>
                     ) : statsError ? (
                        <p className="staffProfileUI__status staffProfileUI__status--error">
                           {statsError}
                        </p>
                     ) : statsView === "tickets" ? (
                           <div className="staffProfileUI__ticketGrid">
                              {ticketVm.map((ticket) => {
                                 const cls =
                                    "staffProfileUI__ticket" +
                                    (ticket.state === "ok"
                                       ? " staffProfileUI__ticket--ok"
                                       : ticket.state === "bad"
                                         ? " staffProfileUI__ticket--bad"
                                         : "");

                                 return (
                                    <button
                                       key={ticket.id}
                                       type="button"
                                       className={cls}
                                       onClick={(event) => event.preventDefault()}
                                       title={`ID: ${ticket.id} • ${ticket.label}`}
                                       aria-disabled="true"
                                    >
                                       <div className="staffProfileUI__ticketTitle">
                                          Bilet {ticket.nr}
                                       </div>
                                       {ticket.scoreText ? (
                                          <div className="staffProfileUI__ticketMeta">
                                             <span className="staffProfileUI__ticketScore">
                                                {ticket.scoreText}
                                             </span>
                                          </div>
                                       ) : null}
                                    </button>
                                 );
                              })}
                           </div>
                     ) : (
                        <>
                              {showCategoryLoading && (
                                 <p className="staffProfileUI__status">
                                    Se încarcă categoriile...
                                 </p>
                              )}

                              {showCategoryError && (
                                 <p className="staffProfileUI__status staffProfileUI__status--error">
                                    {statsCategoriesError}
                                 </p>
                              )}

                              {showCategoryEmpty && (
                                 <div className="staffProfileUI__catEmpty">
                                    Nu există categorii.
                                 </div>
                              )}

                              {showCategoryList && categoryRows.length > 0 && (
                                 <div className="staffProfileUI__catList">
                                    {categoryRows.map((category) => {
                                       const stat = catStatById[String(category.id)] || {
                                          state: "none",
                                          total: Number(category.count) || 0,
                                          correct: null,
                                          st: STATUS.NOT_STARTED,
                                       };

                                       const itemClass =
                                          "staffProfileUI__catItem" +
                                          (stat.state === "ok"
                                             ? " staffProfileUI__catItem--ok"
                                             : "") +
                                          (stat.state === "bad"
                                             ? " staffProfileUI__catItem--bad"
                                             : "");

                                       const badgeClass =
                                          "staffProfileUI__catBadge" +
                                          (stat.state === "ok"
                                             ? " staffProfileUI__catBadge--ok"
                                             : "") +
                                          (stat.state === "bad"
                                             ? " staffProfileUI__catBadge--bad"
                                             : "") +
                                          (stat.state === "none"
                                             ? " staffProfileUI__catBadge--none"
                                             : "");

                                       const totalDisp =
                                          Number(stat.total ?? category.count ?? 0) || 0;
                                       const correctDisp =
                                          Number.isFinite(Number(stat.correct)) &&
                                          Number(stat.correct) >= 0
                                             ? Number(stat.correct)
                                             : null;

                                       const badgeText =
                                          correctDisp != null && totalDisp > 0
                                             ? `${Math.min(correctDisp, totalDisp)}/${totalDisp}`
                                             : statusLabelRo(stat.st);

                                       return (
                                          <button
                                             key={category.id}
                                             type="button"
                                             className={itemClass}
                                             onClick={(event) => event.preventDefault()}
                                             aria-disabled="true"
                                             title={category.name}
                                          >
                                             <div className="staffProfileUI__catLeft">
                                                <div className="staffProfileUI__catTitle">
                                                   {(
                                                      category.titleParts?.length
                                                         ? category.titleParts
                                                         : [
                                                              {
                                                                 marker: null,
                                                                 text: category.name,
                                                              },
                                                           ]
                                                   ).map((part, index) => (
                                                      <span
                                                         key={`${category.id}-${index}`}
                                                         className="staffProfileUI__catLine"
                                                      >
                                                         {part.marker ? (
                                                            <span className="staffProfileUI__catLineMark">
                                                               {part.marker}
                                                            </span>
                                                         ) : null}
                                                         {part.text ? (
                                                            <span className="staffProfileUI__catLineText">
                                                               {part.text}
                                                            </span>
                                                         ) : null}
                                                      </span>
                                                   ))}
                                                </div>
                                             </div>

                                             <div className={badgeClass}>
                                                {badgeText}
                                             </div>
                                          </button>
                                       );
                                    })}
                                 </div>
                              )}
                        </>
                     )}
                  </div>
               </div>
            )}

            {tab === "groups" && isProfessorMode && (
               <div className="staffProfileUI__section">
                  <div className="staffProfileUI__groups">
                     <div className="staffProfileUI__groupsTitle">
                        Grupe predate
                     </div>

                     {groupsStatus === "loading" && (
                        <p className="staffProfileUI__groupsState">
                           Se încarcă grupele...
                        </p>
                     )}

                     {groupsStatus === "failed" && groupsError && (
                        <p className="staffProfileUI__groupsState">
                           {groupsError}
                        </p>
                     )}

                     {groupsStatus !== "loading" &&
                        groupsStatus !== "failed" &&
                        professorGroups.length === 0 && (
                           <p className="staffProfileUI__groupsState">
                              Nu are grupe atribuite.
                           </p>
                        )}

                     {groupsStatus !== "loading" &&
                        groupsStatus !== "failed" &&
                        professorGroups.length > 0 && (
                           <div className="staffProfileUI__groupList">
                              {professorGroups.map((group) => {
                                 const membersLabel = formatMembersLabel(
                                    group.membersCount,
                                 );

                                 return (
                                    <div
                                       key={group.id || group.name}
                                       className="staffProfileUI__groupItem"
                                    >
                                       <span className="staffProfileUI__groupName">
                                          {group.name}
                                       </span>
                                       {membersLabel ? (
                                          <span className="staffProfileUI__groupMeta">
                                             {membersLabel}
                                          </span>
                                       ) : null}
                                    </div>
                                 );
                              })}
                           </div>
                        )}
                  </div>
               </div>
            )}
         </div>
      </div>
   );
}
